import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { EcranConnexion } from './ecrans/EcranConnexion.jsx';
import { EcranTerrain } from './ecrans/EcranTerrain.jsx';
import { EcranEffectif } from './ecrans/EcranEffectif.jsx';
import { EcranMemoire } from './ecrans/EcranMemoire.jsx';
import { EcranTactique } from './ecrans/EcranTactique.jsx';
import { BarreOnglets } from './composants/BarreOnglets.jsx';
import { BanniereMaj } from './composants/BanniereMaj.jsx';
import { appliquerTheme } from './theme.js';
import { lireInstantane, rafraichirInstantane, brancherSynchro, recalerDepuisServeur, sauverMatch, listerMatchs, sauverBrouillon, lireBrouillon, oublierBrouillon } from './local/depot.js';
import * as horloge from './local/chrono.js';
import { matchVide } from './logique/match.js';
import { nouvelleCompo, changerFormation, creneaux } from './logique/compo.js';
import { CHIPS, FORMATIONS } from './data/formations.js';
import { historiqueUtile } from './logique/historique.js';
import { statistiquesParJoueur } from './logique/statistiques.js';
import { besoinMiseAJour } from './logique/fraicheur.js';

const THEME_CLE = 'aja.theme';

export function App() {
  const [session, setSession] = useState('inconnue');   // inconnue | ouverte | fermee
  const [instantane, setInstantane] = useState(null);
  const [etatReseau, setEtatReseau] = useState('chargement');
  const [onglet, setOnglet] = useState('terrain');
  const [compo, setCompo] = useState(null);
  const [brouillonLu, setBrouillonLu] = useState(false);
  const [match, setMatch] = useState(() => matchVide(null));
  const [chrono, setChrono] = useState(() => horloge.lireChrono());
  const [mesMatchs, setMesMatchs] = useState([]);
  const [paysage, setPaysage] = useState(false);
  const [tactiqueFermee, setTactiqueFermee] = useState(false);
  const [theme, setTheme] = useState(() => {
    try { return localStorage.getItem(THEME_CLE) ?? 'sombre'; } catch { return 'sombre'; }
  });

  /**
   * Le téléphone tourné ouvre le tableau tactique.
   *
   * C'est le geste que décrivait la maquette — « téléphone à l'horizontale » — et il
   * n'a pas besoin de bouton : tourner l'appareil EST l'intention. Le manifeste a dû
   * être rouvert à la rotation, il la verrouillait en portrait.
   *
   * « Fermer » repasse en portrait jusqu'au prochain aller-retour : sans cette
   * mémoire, l'écran se rouvrirait aussitôt puisque l'appareil, lui, n'a pas bougé.
   */
  useEffect(() => {
    const m = window.matchMedia('(orientation: landscape)');
    const suivre = () => { setPaysage(m.matches); if (!m.matches) setTactiqueFermee(false); };
    suivre();
    m.addEventListener('change', suivre);
    return () => m.removeEventListener('change', suivre);
  }, []);

  useEffect(() => {
    appliquerTheme(theme);
    try { localStorage.setItem(THEME_CLE, theme); } catch { /* mode privé */ }
  }, [theme]);

  // Le chrono est sauvé à chaque passage en arrière-plan : iOS peut tuer
  // l'application sans prévenir, et sans événement.
  useEffect(() => horloge.brancherChrono(() => chrono), [chrono]);

  // Démarrage : l'instantané local d'abord, pour que l'écran s'affiche PLEIN avant
  // même de savoir s'il y a du réseau. Le réseau, ensuite, ne fait que l'améliorer.
  //
  // C'est une fonction et non un effet piloté par `session` : un effet dépendant de
  // l'état qu'il met lui-même à jour se relançait une seconde fois dès la session
  // ouverte, et l'application téléchargeait l'instantané deux fois à chaque lancement.
  const charger = useCallback(async () => {
    const local = await lireInstantane().catch(() => null);
    if (local) { setInstantane(local); setEtatReseau('local'); }

    const r = await rafraichirInstantane();
    if (r.etat === 'deconnecte') {
      setSession('fermee');
      if (r.instantane) setInstantane(r.instantane);
      setEtatReseau('hors-ligne');
      return;
    }
    setSession('ouverte');
    if (r.instantane) setInstantane(r.instantane);
    setEtatReseau(r.etat);
    brancherSynchro();
    recalerDepuisServeur();
  }, []);

  useEffect(() => { charger(); }, [charger]);

  /**
   * Toute écriture passe par UN enregistrement de match.
   *
   * Composition et journal étaient deux écritures distinctes jointes par un
   * identifiant de rencontre venu du fournisseur ; sans calendrier il valait
   * « libre » pour tout le monde et le deuxième match écrasait le premier.
   */
  const relireMatchs = useCallback(() => { listerMatchs().then(setMesMatchs).catch(() => {}); }, []);
  useEffect(() => { relireMatchs(); }, [relireMatchs]);

  // L'historique qui nourrit le pré-remplissage : tes onze affirmés d'abord, ceux du
  // fournisseur ensuite. Voir src/logique/historique.js.
  const historique = useMemo(
    () => historiqueUtile({ fournisseur: instantane?.historique ?? [], matchs: mesMatchs }),
    [instantane, mesMatchs]
  );

  /**
   * L'état courant, lisible depuis n'importe quel rappel.
   *
   * `enregistrerMatch` capturait `compo` et `match` de son rendu. Lors d'un
   * changement, l'écran envoie deux mises à jour coup sur coup — le journal puis la
   * composition — et l'enregistrement partait avec l'ancien onze : le match archivé
   * gardait le sortant sur le terrain. Des références suppriment la classe entière de
   * fautes au lieu de ce cas-là, et rendent le rappel stable.
   */
  const compoRef = useRef(compo);
  const matchRef = useRef(match);
  compoRef.current = compo;
  matchRef.current = match;

  const enregistrerMatch = useCallback(async (patch = {}) => {
    const c = compoRef.current;
    if (!c) return null;
    const enr = await sauverMatch({
      id: c.id,
      cree_a: c.creeA,
      date: c.date,
      adversaire: c.adversaire,
      domicile: c.domicile,
      formation: c.formation,
      places: c.places,
      evenements: matchRef.current.evenements,
      etat: c.etat,
      ...patch,
    });
    setCompo((x) => (x ? { ...x, id: enr.id, creeA: enr.cree_a } : x));
    relireMatchs();
    return enr;
  }, [relireMatchs]);

  // Chaque événement de match est enregistré aussitôt : iOS peut tuer l'application
  // sans prévenir, et un but saisi qui disparaît est exactement ce qu'on ne pardonne pas.
  const surMatch = useCallback((m) => {
    setMatch(m);
    matchRef.current = m;
    if (compoRef.current?.etat === 'probable') return;   // pas encore un vrai match
    enregistrerMatch({ evenements: m.evenements }).catch(() => {});
  }, [enregistrerMatch]);

  /**
   * UN CHANGEMENT EST UNE SEULE OPÉRATION.
   *
   * Le journal et la composition changent ensemble, et ils doivent être enregistrés
   * ensemble. Envoyés séparément, le premier écrit partait avec l'état d'avant le
   * second — et le match archivé montrait le sortant encore en jeu.
   */
  const surChangement = useCallback((evenement, places) => {
    const m = { ...matchRef.current, evenements: [...matchRef.current.evenements, evenement].sort((a, b) => (a.minute ?? 0) - (b.minute ?? 0)), maj_a: Date.now() };
    const c = { ...compoRef.current, places };
    matchRef.current = m;
    compoRef.current = c;
    setMatch(m);
    setCompo(c);
    if (c.etat === 'probable') return;
    enregistrerMatch({ evenements: m.evenements, places }).catch(() => {});
  }, [enregistrerMatch]);

  /**
   * L'effectif enrichi de TES statistiques.
   *
   * Aucun fournisseur gratuit ne donne celles de la saison en cours. Elles se
   * calculent donc depuis tes propres matchs : le onze affirmé donne les
   * titularisations et, avec les changements, les minutes exactes ; le journal donne
   * buts, passes et cartons ; et le créneau occupé à chaque titularisation donne la
   * répartition des postes de la fiche.
   */
  const effectifStatte = useMemo(() => {
    const base = instantane?.effectif ?? [];
    const calcul = statistiquesParJoueur({
      historique,
      journaux: mesMatchs.map((m) => ({ matchId: m.id, evenements: m.evenements })),
    });
    if (!calcul.size) return base;
    return base.map((j) => {
      const s = calcul.get(j.id);
      if (!s) return j;
      return {
        ...j,
        stats: { minutes: s.minutes, matchs: s.matchs, titularisations: s.titularisations, buts: s.buts, passes: s.passes },
        jaunesSaison: s.jaunes,
        rougesSaison: s.rouges,
        // La répartition mesurée l'emporte sur celle du fournisseur : elle vient des
        // onze que tu as affirmés, pas d'une grille interprétée.
        repartition: s.repartition.length ? s.repartition : j.repartition,
      };
    });
  }, [instantane, historique, mesMatchs]);

  const instantaneStatte = useMemo(
    () => (instantane ? { ...instantane, effectif: effectifStatte } : null),
    [instantane, effectifStatte]
  );

  // La compo naît pré-remplie sur la formation du DERNIER match joué, pas sur un
  // 4-3-3 par défaut : le Terrain vide n'a aucune valeur, le Terrain rempli en a toute.
  useEffect(() => {
    if (!instantane || compo || brouillonLu) return;
    let vivant = true;
    lireBrouillon().then((b) => {
      if (!vivant) return;
      setBrouillonLu(true);
      // Le brouillon reprend la main sur le pré-remplissage : ce sont TES corrections.
      // On vérifie quand même sa formation, un fichier de données ayant pu la retirer
      // entre deux versions.
      if (b?.formation && CHIPS.concat(Object.keys(FORMATIONS)).includes(b.formation)) {
        const p = instantane.prochain?.local ?? null;
        // Un brouillon écrit avant que la compo porte son match n'a pas d'adversaire :
        // on le complète avec la prochaine journée plutôt que de le laisser anonyme.
        setCompo(b.adversaire ? b : {
          ...b,
          adversaire: p?.adversaire ?? null,
          domicile: b.domicile ?? p?.domicile !== false,
          date: b.date ?? p?.date ?? new Date().toISOString().slice(0, 10),
        });
        return;
      }
      const f = CHIPS.includes(instantane.formationRecente) ? instantane.formationRecente : CHIPS[0];
      // LA COMPO PORTE LE MATCH AUQUEL ELLE SE RAPPORTE, dès sa naissance.
      //
      // Elle naissait sans adversaire ni date pendant que l'en-tête affichait la
      // prochaine journée : tout paraissait juste, et « C'est la compo » archivait un
      // « Match sans adversaire » que Mémoire ne rattachait à aucune journée. Pire,
      // deux matchs sans adversaire partageaient la même clé de rencontre et
      // s'écrasaient l'un l'autre dans l'historique.
      const p = instantane.prochain?.local ?? null;
      setCompo({
        ...nouvelleCompo(f, { historique, effectif: instantane.effectif }),
        adversaire: p?.adversaire ?? null,
        domicile: p?.domicile !== false,
        date: p?.date ?? new Date().toISOString().slice(0, 10),
      });
    }).catch(() => setBrouillonLu(true));
    return () => { vivant = false; };
  }, [instantane, compo, brouillonLu, historique]);

  // Le brouillon suit l'écran. Écrit à chaque changement, il n'a pas besoin d'être
  // débattu : une composition pèse quelques centaines d'octets et IndexedDB est
  // asynchrone, donc rien ne bloque le doigt qui déplace un jeton.
  useEffect(() => {
    if (!compo) return;
    sauverBrouillon(compo).catch(() => {});
  }, [compo]);

  const surFormation = useCallback((f) => {
    setCompo((c) => (c ? changerFormation(c, f, { effectif: instantane?.effectif ?? [], historique }) : c));
  }, [instantane, historique]);

  const surChrono = useCallback(() => {
    setChrono((c) => (c.etat === 'marche' ? horloge.pauser(c) : horloge.demarrer(c, instantane?.prochain?.id ?? null)));
  }, [instantane]);

  const surMiTemps = useCallback(() => setChrono((c) => horloge.secondePeriode(c)), []);

  const adversaire = useCallback(() => {
    const m = instantane?.prochain;
    if (!m) return null;
    return String(m.domicile?.id) === String(instantane?.equipe?.id) ? m.exterieur?.nom : m.domicile?.nom;
  }, [instantane]);

  const confirmer = useCallback(async () => {
    if (compoRef.current) compoRef.current = { ...compoRef.current, etat: 'annoncee' };
    await enregistrerMatch({ etat: 'annoncee' });
    setCompo((c) => (c ? { ...c, etat: 'annoncee' } : c));
  }, [enregistrerMatch]);

  /** Le coup d'envoi : l'état avance ET le chrono part, d'un seul geste. */
  const coupDEnvoi = useCallback(async () => {
    setChrono((c) => horloge.demarrer(c, compoRef.current?.id ?? null));
    setCompo((c) => (c ? { ...c, etat: 'en-cours' } : c));
    if (compoRef.current) compoRef.current = { ...compoRef.current, etat: 'en-cours' };
    await enregistrerMatch({ etat: 'en-cours' });
  }, [enregistrerMatch]);

  const finDuMatch = useCallback(async () => {
    // Le chrono s'arrête AVANT l'enregistrement : un match terminé dont le chrono
    // tourne encore rebasculerait aussitôt en « en cours ».
    setChrono((c) => horloge.pauser(c));
    if (compoRef.current) compoRef.current = { ...compoRef.current, etat: 'jouee' };
    await enregistrerMatch({ etat: 'jouee' });
    setCompo((c) => (c ? { ...c, etat: 'jouee' } : c));
  }, [enregistrerMatch]);

  /** Une nouvelle journée : adversaire et lieu sont demandés, ils sont la moitié du sens. */
  const nouveauMatch = useCallback((infos = {}) => {
    oublierBrouillon().catch(() => {});
    setChrono(horloge.reinitialiser(null));
    setMatch(matchVide(null));
    const f = CHIPS.includes(instantane?.formationRecente) ? instantane.formationRecente : CHIPS[0];
    setCompo({
      ...nouvelleCompo(f, { historique, effectif: instantane?.effectif ?? [] }),
      id: undefined,
      creeA: undefined,
      date: infos.date ?? new Date().toISOString().slice(0, 10),
      adversaire: infos.adversaire ?? null,
      domicile: infos.domicile !== false,
    });
    setOnglet('terrain');
  }, [instantane, historique]);

  /**
   * REPRENDRE LE ONZE D'UN MATCH ARCHIVÉ — on le copie, on ne l'ouvre pas.
   *
   * L'ancien comportement chargeait le match archivé dans la compo de travail, donc
   * le rendait modifiable : « Modifier » le ramenait à l'état de brouillon, « Vider »
   * réapparaissait, et on pouvait effacer le onze d'un match joué il y a trois
   * semaines — qui sert d'historique au pré-remplissage. Un archivé se consulte.
   *
   * Ce qui est utile, c'est de repartir de son onze pour préparer le prochain match.
   * C'est donc une COPIE dans le brouillon courant, qui garde l'adversaire et la date
   * du match à venir, pas ceux du match repris.
   */
  const reprendreOnze = useCallback((m) => {
    const base = creneaux(m.formation);
    for (const p of m.places ?? []) if (base[p.index]) base[p.index].joueurId = p.joueurId;
    setCompo((c) => ({
      formation: m.formation,
      places: base,
      phase: 'offensive',
      etat: 'probable',
      // Ni id ni creeA : c'est un nouveau brouillon, pas l'archive rouverte.
      adversaire: c?.adversaire ?? null,
      domicile: c?.domicile ?? true,
      date: c?.date ?? new Date().toISOString().slice(0, 10),
    }));
    setOnglet('terrain');
  }, []);

  /**
   * Préparer une journée depuis Mémoire.
   *
   * Elle appelait « nouveau match », qui jette le brouillon et repré-remplit : un
   * terrain qu'on venait de vider exprès se retrouvait repeuplé, en silence. Or
   * toucher une journée qu'on prépare déjà ne veut pas dire « recommence », ça veut
   * dire « montre-la-moi ».
   *
   * Même rencontre : on retourne simplement au Terrain. Rencontre différente avec du
   * travail en cours : on demande, parce qu'on s'apprête à le jeter.
   */
  const preparerJournee = useCallback((j) => {
    const c = compoRef.current;
    const meme = c && String(c.adversaire ?? '').toLowerCase() === String(j.adversaire ?? '').toLowerCase();
    if (meme) { setOnglet('terrain'); return; }

    const poses = c?.places?.filter((p) => p.joueurId).length ?? 0;
    if (c?.etat === 'probable' && poses > 0
      && !confirm(`Préparer ${j.adversaire} ? La composition en cours sera remplacée.`)) return;

    nouveauMatch({ adversaire: j.adversaire, domicile: j.domicile, date: j.date });
  }, [nouveauMatch]);

  /** Repartir du pré-remplissage : tout le monde se trompe en corrigeant. */
  const reprefiller = useCallback(() => {
    setCompo((c) => (c ? {
      ...c,
      places: nouvelleCompo(c.formation, { historique, effectif: instantane?.effectif ?? [] }).places,
    } : c));
  }, [historique, instantane]);

  if (session === 'inconnue') return <Attente />;
  if (session === 'fermee') return <EcranConnexion onConnecte={charger} />;
  if (!instantane || !compo) return <Attente texte={etatReseau === 'vide' ? 'Aucune donnée. Une première connexion au réseau est nécessaire.' : null} />;

  const fraicheur = phraseFraicheur(instantane, etatReseau);

  // Un écran large n'est pas un téléphone couché : sur une tablette ou un ordinateur,
  // le portrait reste le bon format et le tableau tactique s'ouvre à la demande.
  const tactique = paysage && !tactiqueFermee && onglet === 'terrain' && window.innerWidth < 1100;

  return (
    <div style={{ position: 'relative', height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {tactique && (
        <EcranTactique
          instantane={instantaneStatte}
          compo={compo}
          match={match}
          chrono={chrono}
          onCompo={setCompo}
          onFermer={() => setTactiqueFermee(true)}
        />
      )}

      {onglet === 'terrain' && (
        <EcranTerrain
          instantane={instantaneStatte} compo={compo} match={match} chrono={chrono} theme={theme} fraicheur={fraicheur}
          onCompo={setCompo} onMatch={surMatch} onChangement={surChangement} onChrono={surChrono} onMiTemps={surMiTemps}
          onTheme={() => setTheme((t) => (t === 'sombre' ? 'clair' : 'sombre'))}
          onFormation={surFormation}
          onReprefiller={reprefiller}
          onConfirmer={confirmer} onCoupDEnvoi={coupDEnvoi} onFinDuMatch={finDuMatch} onNouveauMatch={nouveauMatch}
        />
      )}
      {onglet === 'effectif' && <EcranEffectif instantane={instantaneStatte} fraicheur={fraicheur} />}
      {onglet === 'memoire' && <EcranMemoire instantane={instantane} matchs={mesMatchs} historique={historique} onOuvrir={reprendreOnze} onChangement={relireMatchs}
        brouillon={compo} onPreparer={preparerJournee} />}

      <BanniereMaj matchEnCours={chrono.etat === 'marche'} />
      {/* Jamais pendant un match : à la 70e minute, une pastille de maintenance n'a
          rien à faire sous le pouce. */}
      <BarreOnglets
        actif={onglet}
        onChanger={setOnglet}
        alerte={chrono.etat === 'arrete' && besoinMiseAJour(instantane ?? {}).niveau === 'necessaire' ? 'memoire' : null}
      />
    </div>
  );
}

const Attente = ({ texte }) => (
  <div style={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 14, padding: 30, textAlign: 'center' }}>
    <img src="/icones/aja.svg" alt="" width="64" height="64" style={{ borderRadius: 17, opacity: .9 }} />
    {texte && <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.5, color: 'var(--ink2)', maxWidth: 260 }}>{texte}</p>}
  </div>
);

/**
 * Dire DE QUAND datent les données, toujours.
 *
 * Sans cette phrase, l'utilisateur ne peut pas savoir si l'application lui ment : un
 * effectif d'il y a trois jours a exactement la même tête qu'un effectif frais.
 */
function phraseFraicheur(instantane, etat) {
  const dates = Object.values(instantane.fraicheur ?? {}).map((f) => f.recuA).filter(Boolean);
  const local = Object.values(instantane.fraicheur ?? {}).every((f) => f.origine === 'local');
  if (local) return 'données de démonstration';
  if (!dates.length) return etat === 'hors-ligne' ? 'hors-ligne' : 'données locales';

  const plusVieux = Math.min(...dates);
  const minutes = Math.round((Date.now() - plusVieux) / 60000);
  const quand = minutes < 2 ? "à l'instant"
    : minutes < 60 ? `il y a ${minutes} min`
    : minutes < 60 * 36 ? `il y a ${Math.round(minutes / 60)} h`
    : new Date(plusVieux).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });

  return etat === 'hors-ligne' ? `hors-ligne · ${quand}` : `à jour ${quand}`;
}
